import { c as _c } from "react/compiler-runtime";
import { Fragment, useContext, useState } from 'react';
import { Route, useHistory } from 'react-router';
import useSWR from 'swr';
import { APITeam } from '../../../../../../../../../typings/Team.interface';
import APIMethodKeys from '../../../../../../client/APIMethodKeys';
import { useCopyToClipboard } from '../../../../../../utils/copy';
import { showChat } from '../../../../../../utils/liveChat';
import Alert from '../../../../../common/base/Alert';
import Badge from '../../../../../common/base/Badge';
import Button from '../../../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../../../common/base/Card';
import Divider from '../../../../../common/base/Divider';
import Icon from '../../../../../common/base/Icon';
import Link from '../../../../../common/base/Link';
import Skeleton from '../../../../../common/base/Skeleton';
import Text from '../../../../../common/base/Text';
import Tooltip from '../../../../../common/base/Tooltip';
import { useDialog } from '../../../../../common/Dialog';
import { StyledSwitch } from '../../../../../common/Form/Fields/SwitchInput';
import ScrollArea from '../../../../../common/helpers/ScrollArea';
import { Div } from '../../../../../common/helpers/StyledUtils';
import Search from '../../../../../common/Search';
import { useToasts } from '../../../../../common/Toast';
import { GlobalContext } from '../../../../../contexts/GlobalContext';
import { DashboardContext } from '../../../DashboardContext';
import EnableStaticIPDialog, { eg_static_ip_addresses } from '../../Team/EnableStaticIPDialog';
import OutpostStaticIPSections from '../../Team/OutpostStaticIPSections';
import DisableStaticIPOrgDialog from './DisableStaticIPOrgDialog';
interface AddonCardProps {
  enabled_teams: APITeam[];
  all_teams: APITeam[];
  addon_price: {
    unit_amount: number;
    currency: string;
  } | null | undefined;
  is_price_loading: boolean;
  has_payment_method: boolean;
  can_toggle: boolean;
  is_admin: boolean;
  is_custom_enterprise: boolean;
  onEnable: () => void;
  onDisable: () => void;
}
interface AddonUIConfig {
  key: string;
  team_flag: keyof APITeam;
  EnableDialog: React.FC<{
    onClose: () => void;
  }>;
  DisableDialog: React.FC<{
    affected_teams: APITeam[];
    onClose: () => void;
  }>;
  CardComponent: React.FC<AddonCardProps>;
}
const formatPrice = (cents: number, currency: string) => {
  const amount = cents / 100;
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase()
  });
  return formatter.format(amount);
};
const StaticIPAddonCard: React.FC<AddonCardProps> = ({
  all_teams,
  addon_price,
  is_price_loading,
  can_toggle,
  is_admin,
  has_payment_method,
  is_custom_enterprise,
  onEnable,
  onDisable
}) => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    mutateTeam,
    mutateTeams,
    team: current_team
  } = useContext(DashboardContext);
  const {
    addToast
  } = useToasts();
  const showDialog = useDialog();
  const copyToClipboard = useCopyToClipboard();
  const [show_project_list, setShowProjectList] = useState(false);
  const [search, setSearch] = useState('');
  // Tracks in-flight toggles: team_id → intended static_ip_proxy state (optimistic)
  const [pending_toggles, setPendingToggles] = useState<Record<string, boolean>>({});
  const getTeamEnabled = (t: APITeam) => t.id in pending_toggles ? pending_toggles[t.id] : !!t.static_ip_proxy;
  const enabled_count = all_teams.filter(t_0 => getTeamEnabled(t_0)).length;
  const is_enabled = enabled_count > 0;
  const filtered_teams = (() => {
    const query = search.toLowerCase().trim();
    const matched = query ? all_teams.filter(t_1 => t_1.name.toLowerCase().includes(query)) : all_teams;
    return [...matched].sort((a, b) => {
      if (a.id === current_team?.id) return -1;
      if (b.id === current_team?.id) return 1;
      return a.name.localeCompare(b.name);
    });
  })();
  const executeToggle = async (target_team: APITeam, enable: boolean) => {
    setPendingToggles(prev => ({
      ...prev,
      [target_team.id]: enable
    }));
    try {
      if (enable) {
        // Pass confirmed_price to validate the price hasn't changed
        await HookdeckAPI.billing.enableAddonBulk('static_ip', [target_team.id], addon_price?.unit_amount);
      } else {
        await HookdeckAPI.billing.disableAddonBulk('static_ip', [target_team.id]);
      }
      if (target_team.id === current_team?.id) {
        mutateTeam({
          ...current_team!,
          static_ip_proxy: enable
        });
      }
      addToast('success', `Static IP delivery ${enable ? 'enabled' : 'disabled'} for ${target_team.name}.`);
      // Wait for teams refetch to complete before clearing the optimistic override
      await (mutateTeams as () => Promise<unknown>)?.();
    } catch (error: any) {
      const error_message = error?.response?.message || `Failed to ${enable ? 'enable' : 'disable'} Static IP. Please try again.`;
      addToast('error', error_message);
      throw error;
    } finally {
      setPendingToggles(prev => {
        const {
          [target_team.id]: _,
          ...rest
        } = prev;
        return rest;
      });
    }
  };
  const handleToggleTeam = (target_team_0: APITeam, enable_0: boolean) => {
    if (enable_0) {
      executeToggle(target_team_0, true);
    } else {
      showDialog(() => executeToggle(target_team_0, false), undefined, {
        title: 'Disable Static IP delivery',
        submit_label: enabled_count === 1 ? 'Disable Static IP delivery' : 'Disable for 1 project',
        cancel_label: 'Cancel',
        danger: true,
        message: <Div>
            {enabled_count === 1 && <Alert danger inline m={{
            b: 4
          }}>
                <Text bold>This action will disable Static IP delivery on your organization</Text>
                <Text>
                  This is the sole project supporting Static IP delivery on this organization.
                  Disabling will also turn off the feature at the organization level.
                </Text>
              </Alert>}
            <Text as="p" m={0}>
              Are you sure you&apos;d like to disable Static IP delivery on{' '}
              <Text as="span" semi>
                {target_team_0.name}
              </Text>
              ? If destinations on this project have IP allowlisting configured, webhook deliveries
              may begin to fail.
            </Text>
          </Div>
      });
    }
  };
  return <StyledCard m={{
    b: 4
  }}>
      <StyledCardSection flex={{
      justify: 'space-between',
      align: 'center'
    }} p={{
      x: 4,
      y: 3
    }}>
        <Div flex={{
        align: 'center',
        gap: 3
      }}>
          {can_toggle ? <StyledSwitch as="label" has_label={false} onClick={e => {
          e.preventDefault();
          if (is_enabled) {
            onDisable();
          } else {
            onEnable();
          }
        }}>
              <input checked={is_enabled} type="checkbox" readOnly />
            </StyledSwitch> : <Tooltip tooltip={!has_payment_method ? 'No payment method found. Please add a payment method to enable addons.' : !is_admin ? 'Only organization admins can toggle addons.' : undefined} disabled={is_custom_enterprise}>
              <StyledSwitch as="label" has_label={false} disabled>
                <input checked={is_enabled} disabled type="checkbox" />
              </StyledSwitch>
            </Tooltip>}
          <Div flex={{
          align: 'center',
          gap: 1
        }}>
            <Text>Delivery from Static IPs</Text>
            <Tooltip tooltip="Route webhook deliveries through dedicated IP addresses for allowlisting.">
              <Icon muted icon="info" />
            </Tooltip>
          </Div>
        </Div>
        {is_custom_enterprise ? <Link as="button" onClick={showChat}>
            Contact us to {is_enabled ? 'disable' : 'enable'}
          </Link> : addon_price ? <Div flex>
            <Text semi>{formatPrice(addon_price.unit_amount, addon_price.currency)}</Text>
            <Text muted>/month</Text>
          </Div> : is_price_loading ? <Skeleton w={{
        px: 100
      }} h={{
        px: 24
      }} loading /> : null}
      </StyledCardSection>
      {is_enabled && <StyledCardSection p={4}>
          <StyledCard overflow_hidden>
            {(() => {
          const has_eg = all_teams.some(t_2 => t_2.type !== 'outpost');
          const has_outpost = all_teams.some(t_3 => t_3.type === 'outpost');
          const show_grouped = has_eg && has_outpost;
          return <>
                  {has_eg && <StyledCardSection muted p={3} flex={{
              direction: 'column',
              align: 'flex-start',
              gap: 3
            }}>
                      <Div w={100} flex={{
                align: 'center',
                justify: 'space-between',
                gap: 2
              }}>
                        <Text semi size="s">
                          {show_grouped ? 'Event Gateway IP addresses' : 'Dedicated Hookdeck IP addresses'}
                        </Text>
                        <Button ghost icon="copy" m={-1} onClick={() => copyToClipboard(eg_static_ip_addresses.join(', '))} />
                      </Div>
                      <Div flex={{
                align: 'flex-start',
                gap: 2,
                wrap: true
              }}>
                        {eg_static_ip_addresses.map(address => <Badge mono subtle neutral key={address}>
                            {address}
                          </Badge>)}
                      </Div>
                    </StyledCardSection>}
                  {has_outpost && <OutpostStaticIPSections show_grouped={show_grouped} />}
                </>;
        })()}
          </StyledCard>
          <StyledCard m={{
        t: 4
      }} overflow_hidden>
            <StyledCardSection p={3} flex={{
          direction: 'column',
          gap: 2
        }}>
              <Div flex={{
            align: 'center',
            gap: 2
          }}>
                <Button ghost small icon={show_project_list ? 'chevron_up' : 'chevron_down'} onClick={() => setShowProjectList(!show_project_list)} />
                <Text semi>
                  Enabled on {enabled_count} {enabled_count !== 1 ? 'projects' : 'project'}
                </Text>
              </Div>
              {show_project_list && <Search small value={search} onChange={setSearch} placeholder={`Filter ${all_teams.length} projects...`} />}
            </StyledCardSection>
            {show_project_list && <>
                <Divider />
                <ScrollArea max_h={{
            px: 174
          }} p={1}>
                  {(() => {
              const eg_teams = filtered_teams.filter(t_4 => t_4.type !== 'outpost');
              const outpost_teams = filtered_teams.filter(t_5 => t_5.type === 'outpost');
              const has_both = eg_teams.length > 0 && outpost_teams.length > 0;
              const has_pending = Object.keys(pending_toggles).length > 0;
              const renderTeam = (t_6: APITeam) => {
                const is_team_enabled = getTeamEnabled(t_6);
                return <Div key={t_6.id} p={{
                  x: 2,
                  y: 1.5
                }} flex={{
                  align: 'center',
                  justify: 'space-between',
                  gap: 2
                }}>
                          <Div flex={{
                    align: 'center',
                    gap: 2
                  }}>
                            <StyledSwitch as="label" has_label={false} disabled={has_pending} onClick={e_0 => {
                      e_0.preventDefault();
                      if (!has_pending) {
                        handleToggleTeam(t_6, !is_team_enabled);
                      }
                    }}>
                              <input checked={is_team_enabled} disabled={has_pending} type="checkbox" readOnly />
                            </StyledSwitch>
                            <Text>{t_6.name}</Text>
                            {t_6.id === current_team?.id && <Badge subtle neutral>
                                Current project
                              </Badge>}
                          </Div>
                        </Div>;
              };
              return <>
                        {eg_teams.length > 0 && <>
                            {has_both && <Div p={{
                    x: 2,
                    y: 1
                  }}>
                                <Text muted semi size="xs">
                                  Event Gateway
                                </Text>
                              </Div>}
                            {eg_teams.map(renderTeam)}
                          </>}
                        {outpost_teams.length > 0 && <>
                            {has_both && <Div p={{
                    x: 2,
                    y: 1
                  }}>
                                <Text muted semi size="xs">
                                  Outpost
                                </Text>
                              </Div>}
                            {outpost_teams.map(renderTeam)}
                          </>}
                      </>;
            })()}
                  {filtered_teams.length === 0 && <Div p={{
              x: 3,
              y: 4
            }} flex={{
              justify: 'center'
            }}>
                      <Text muted>No projects found</Text>
                    </Div>}
                </ScrollArea>
              </>}
          </StyledCard>
        </StyledCardSection>}
    </StyledCard>;
};
const ADDON_UI_CONFIGS: AddonUIConfig[] = [{
  key: 'static_ip',
  team_flag: 'static_ip_proxy',
  EnableDialog: EnableStaticIPDialog,
  DisableDialog: DisableStaticIPOrgDialog,
  CardComponent: StaticIPAddonCard
}];
const ADDONS_BASE_PATH = '/settings/organization/billing/addons';
const Addons: React.FC = () => {
  const $ = _c(31);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    organization,
    teams,
    team_role
  } = useContext(DashboardContext);
  const history = useHistory();
  const [active_disable_key, setActiveDisableKey] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = APIMethodKeys.billing.getAddons();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== HookdeckAPI.billing) {
    t1 = () => HookdeckAPI.billing.getAddons();
    $[1] = HookdeckAPI.billing;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: addons_data,
    isLoading: is_price_loading
  } = useSWR(t0, t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = APIMethodKeys.billing.getCard();
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== HookdeckAPI.billing) {
    t3 = () => HookdeckAPI.billing.getCard();
    $[4] = HookdeckAPI.billing;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data: card_data
  } = useSWR(t2, t3);
  let t4;
  if ($[6] !== active_disable_key || $[7] !== addons_data || $[8] !== card_data || $[9] !== history || $[10] !== is_price_loading || $[11] !== organization?.feature_flags?.disable_throughput_change || $[12] !== organization?.id || $[13] !== team_role || $[14] !== teams) {
    const teams_for_org = teams?.filter(team => team.organization_id === organization?.id);
    const is_custom_enterprise = !!organization?.feature_flags?.disable_throughput_change;
    const is_admin = team_role === "admin";
    const can_toggle = !is_custom_enterprise && is_admin && !!card_data;
    const addon_states = ADDON_UI_CONFIGS.map(config => ({
      config,
      addon_data: addons_data?.find(a => a.key === config.key),
      enabled_teams: teams_for_org?.filter(team_0 => !!team_0[config.team_flag]) || [],
      all_teams: teams_for_org || []
    }));
    let t5;
    let t6;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = {
        justify: "space-between",
        align: "center"
      };
      t6 = {
        b: 4
      };
      $[16] = t5;
      $[17] = t6;
    } else {
      t5 = $[16];
      t6 = $[17];
    }
    let t7;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Div flex={t5} m={t6}><Div><Text bold={true} size="l" as="h2" m={0}>Add-ons</Text><Text muted={true} m={0}>Grant your organization access to premium features.</Text></Div></Div>;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    let t8;
    if ($[19] !== is_custom_enterprise) {
      t8 = is_custom_enterprise && <Alert info={true} m={{
        b: 4
      }}>Addon configuration is managed by our team. Contact us to enable or disable addons.</Alert>;
      $[19] = is_custom_enterprise;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    let t9;
    if ($[21] !== can_toggle || $[22] !== card_data || $[23] !== history || $[24] !== is_admin || $[25] !== is_custom_enterprise || $[26] !== is_price_loading) {
      t9 = t10 => {
        const {
          config: config_0,
          addon_data,
          enabled_teams,
          all_teams
        } = t10;
        const {
          CardComponent
        } = config_0;
        return <CardComponent key={config_0.key} enabled_teams={enabled_teams} all_teams={all_teams} addon_price={addon_data?.price} is_price_loading={is_price_loading} has_payment_method={!!card_data} can_toggle={can_toggle} is_admin={is_admin} is_custom_enterprise={is_custom_enterprise} onEnable={() => history.push(`${ADDONS_BASE_PATH}/${config_0.key}/enable`)} onDisable={() => setActiveDisableKey(config_0.key)} />;
      };
      $[21] = can_toggle;
      $[22] = card_data;
      $[23] = history;
      $[24] = is_admin;
      $[25] = is_custom_enterprise;
      $[26] = is_price_loading;
      $[27] = t9;
    } else {
      t9 = $[27];
    }
    let t10;
    if ($[28] !== active_disable_key || $[29] !== history) {
      t10 = t11 => {
        const {
          config: config_1,
          enabled_teams: enabled_teams_0
        } = t11;
        const {
          EnableDialog,
          DisableDialog
        } = config_1;
        return <Fragment key={config_1.key}><Route path={`${ADDONS_BASE_PATH}/${config_1.key}/enable`} render={() => <EnableDialog onClose={() => history.push("/settings/organization/billing")} />} />{active_disable_key === config_1.key && <DisableDialog affected_teams={enabled_teams_0} onClose={() => setActiveDisableKey(null)} />}</Fragment>;
      };
      $[28] = active_disable_key;
      $[29] = history;
      $[30] = t10;
    } else {
      t10 = $[30];
    }
    t4 = <>{t7}{t8}{addon_states.map(t9)}{addon_states.map(t10)}</>;
    $[6] = active_disable_key;
    $[7] = addons_data;
    $[8] = card_data;
    $[9] = history;
    $[10] = is_price_loading;
    $[11] = organization?.feature_flags?.disable_throughput_change;
    $[12] = organization?.id;
    $[13] = team_role;
    $[14] = teams;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
};
export default Addons;